import React, { useEffect, createContext, useReducer, useState } from 'react';
import { IStoreState, IFilterState } from 'src/@operations/props';
import { dataReducer } from 'src/@operations/reducers';
import { sortReducer } from 'src/@operations/reducers/sortReducer';
import { filterReducer } from 'src/@operations/reducers/filterReducer';
import { tagDataReducer } from 'src/app/reducers/tagDataReducer';
// Hooks
import { usePersistentReducer } from 'src/utils';
// import { useReducerWithLogger } from 'src/utils/storeHelpers';
import {
  dataInitialState,
  sortInitialState,
  filterInitialState,
} from 'src/@operations/reducers/initialStates';
import { tagDataInitialState } from 'src/app/reducers/initialStates';
import {
  tableFiltersToOperationSummaryFilter,
  urlToOpFilterState,
} from 'src/@operations/functions';
import { serializeFiltersToUrl, useParseUrlFilters } from 'src/utils/filters/parseUrlFilters';
import { OperationSummaryFilter } from 'src/app/gql/__generated__/hooks';
import { filterKeyMap } from '../reducers/constants';
import { useHistory, useLocation } from 'react-router-dom';
import { useConfigSelectors } from 'src/app/reducers';
import { transformToKeys } from './utils';

export const OperationStateContext = createContext({
  data: dataInitialState,
  sort: sortInitialState,
  filter: filterInitialState,
});

export const OperationDispatchContext = createContext(null);

export const OperationsStateProvider = ({ children }) => {
  const [dataState, dataDispatch]: any = useReducer<any>(dataReducer, dataInitialState);
  const [sortState, sortDispatch]: any = useReducer<any>(sortReducer, sortInitialState);

  const urlFilterState: OperationSummaryFilter = useParseUrlFilters<OperationSummaryFilter>(
    filterKeyMap
  );

  const storageMechanism = Object.keys(urlFilterState).length > 0 ? 'url' : 'session';

  const urlState = urlToOpFilterState(urlFilterState);

  const combinedFilterState: IFilterState = {
    ...filterInitialState,
    isLoadedFromUrl: !!Object.keys(urlFilterState).length,
    tableFilters: {
      ...filterInitialState.tableFilters,
      ...urlState.tableFilters,
    },
    time: urlState.time,
    pcaPosition: urlState.position,
    filterPca: urlState.position ? true : false,
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [filterState, filterDispatch] = usePersistentReducer(
    filterReducer,
    combinedFilterState,
    'operations.filter.state',
    storageMechanism
  );
  const [tagDataState, tagDataDispatch]: any = useReducer<any>(tagDataReducer, tagDataInitialState);

  const state: IStoreState = {
    data: dataState,
    sort: sortState,
    filter: filterState,
    tagData: tagDataState,
  };

  const dispatchers = [dataDispatch, sortDispatch, filterDispatch, tagDataDispatch];

  const dispatch: any = (action: any) =>
    dispatchers.map((dispatcher: any) => {
      dispatcher(action);
    });

  const location = useLocation();
  const history = useHistory();
  const configSelectors = useConfigSelectors();
  const initialFilters = configSelectors.getOperationFilterSet();
  const [defaultLoad, setDefaultLoad] = useState(true);

  useEffect(() => {
    const filterKeys = transformToKeys(state.filter.tableFilters);
    // If the filter keys match the initial filters, do not update the URL
    if (JSON.stringify(filterKeys) === JSON.stringify(initialFilters) || defaultLoad) {
      // Initial load, do not update URL
      // 2 renders, as there is an init action AFTER the initial load
      setDefaultLoad(false);
      return;
    } else {
      if (defaultLoad) {
        setDefaultLoad(false);
      }
    }
    // Create a clean filter object excluding internal fields
    const filtersForUrl = { ...state.filter.tableFilters };
    const positionForUrl = state.filter.pcaPosition;
    const timeRangeForUrl = state.filter.time;
    const intersectionFilter = { gateIds: state.filter.tableFilters.gateIds };

    const { position, filter } = tableFiltersToOperationSummaryFilter(
      filtersForUrl,
      timeRangeForUrl,
      positionForUrl,
      intersectionFilter
    );
    const isPosValid = position && position.latitude !== 0 && position.longitude !== 0;
    const urlSearchParams = serializeFiltersToUrl(
      {
        ...filter,
        ...(isPosValid && { position }),
        ...(intersectionFilter.gateIds && { gateIds: intersectionFilter.gateIds }),
      },
      filterKeyMap,
      true
    );
    const currentSearch = location.search.substring(1); // Remove leading '?'
    // Only update URL if the search params have actually changed
    if (urlSearchParams !== currentSearch) {
      const newPath = `${location.pathname}${urlSearchParams ? `?${urlSearchParams}` : ''}`;
      history.replace(newPath, location.state);
    }
  }, [state.filter, location.pathname, location.search, location.state, history]);

  return (
    <OperationStateContext.Provider value={state}>
      <OperationDispatchContext.Provider value={dispatch}>
        {children}
      </OperationDispatchContext.Provider>
    </OperationStateContext.Provider>
  );
};
